import type { Deal, DealStage } from '@/types'
import {
  hasScheduledMeeting,
  REUNIAO_REQUIRED_FIELD,
  REUNIAO_REQUIRED_MESSAGE,
} from '@/utils/dealMeetingSchedule'
import {
  FINANCE_GERAR_CONTRATO_MESSAGE,
  isCloserBlockedGerarContratoExit,
} from '@/utils/dealFinanceStageGate'
import { blocksPropostaEnviada, PLATAFORMA_REQUIRED_MESSAGE } from '@/utils/dealPlataformaRequired'

const VALID_TRANSITIONS: Record<DealStage, DealStage[]> = {
  qualificado: ['reuniao_agendada', 'perdido', 'pausado', 'fechamento_futuro'],
  reuniao_agendada: ['reuniao_realizada', 'perdido', 'pausado', 'qualificado', 'fechamento_futuro'],
  reuniao_realizada: ['proposta_elaboracao', 'perdido', 'pausado', 'fechamento_futuro'],
  proposta_elaboracao: ['proposta_enviada', 'perdido', 'pausado', 'fechamento_futuro'],
  proposta_enviada: ['gerar_contrato', 'teste_gratis', 'renegociacao', 'perdido', 'pausado', 'fechamento_futuro'],
  teste_gratis: ['gerar_contrato', 'renegociacao', 'perdido', 'pausado', 'fechamento_futuro'],
  renegociacao: ['proposta_elaboracao', 'proposta_enviada', 'perdido', 'pausado', 'fechamento_futuro'],
  gerar_contrato: ['ajustar_proposta', 'contrato_enviado', 'perdido', 'pausado', 'fechamento_futuro'],
  ajustar_proposta: ['gerar_contrato', 'perdido', 'pausado', 'fechamento_futuro'],
  contrato_enviado: ['handover', 'renegociacao', 'perdido', 'pausado', 'fechamento_futuro'],
  handover: ['ganho', 'renegociacao', 'perdido', 'pausado', 'fechamento_futuro'],
  ganho: [],
  perdido: ['renegociacao'],
  pausado: ['qualificado', 'reuniao_agendada', 'reuniao_realizada', 'proposta_elaboracao', 'proposta_enviada', 'renegociacao', 'teste_gratis', 'gerar_contrato', 'ajustar_proposta', 'contrato_enviado', 'handover', 'fechamento_futuro'],
  fechamento_futuro: ['reuniao_agendada', 'reuniao_realizada', 'proposta_elaboracao', 'proposta_enviada', 'teste_gratis', 'pausado'],
}

const FIELD_LABELS: Record<string, string> = {
  budgetConfirmado: 'Budget confirmado',
  decisorPresente: 'Decisor presente',
  doresIdentificadas: 'Dores identificadas',
  motivoPerda: 'Motivo da perda',
  dataFechamentoPrevisto: 'Data de fechamento prevista',
}

const STAGE_REQUIRED_FIELDS: Partial<Record<DealStage, Array<keyof Deal>>> = {
  perdido: ['motivoPerda'],
  fechamento_futuro: ['dataFechamentoPrevisto'],
}

const FINAL_STAGES = new Set<DealStage>(['ganho', 'perdido'])

function isAllowedExitFromFinalStage(from: DealStage, to: DealStage): boolean {
  return from === 'perdido' && to === 'renegociacao'
}

const REALIZED_MEETING_STATUSES = new Set([
  'Realizada',
  'Concluída',
  'REALIZADA',
  'CONCLUÍDA',
])

function hasRealizedReuniao(deal: Deal): boolean {
  return Boolean(deal.reunioes?.some((r) => REALIZED_MEETING_STATUSES.has(r.status)))
}

function hasValue(value: unknown): boolean {
  if (value === undefined || value === null) return false
  if (typeof value === 'string') return value.trim().length > 0
  return true
}

export interface TransitionCheckResult {
  allowed: boolean
  reason?: string
  missingFields?: string[]
  code?: string
}

export function checkTransition(
  deal: Deal,
  targetStage: DealStage,
  userRole?: string | null,
): TransitionCheckResult {
  const currentStage = deal.etapa
  const allowedStages = VALID_TRANSITIONS[currentStage] ?? []

  if (
    FINAL_STAGES.has(currentStage) &&
    currentStage !== targetStage &&
    !isAllowedExitFromFinalStage(currentStage, targetStage)
  ) {
    return {
      allowed: false,
      code: 'DEAL_CLOSED',
      reason: 'Não é possível mudar a etapa de um deal finalizado',
    }
  }

  if (currentStage !== targetStage && !allowedStages.includes(targetStage)) {
    return {
      allowed: false,
      code: 'INVALID_TRANSITION',
      reason: `Transição inválida: de ${currentStage} para ${targetStage}`,
    }
  }

  if (
    currentStage === 'qualificado' &&
    targetStage === 'reuniao_agendada' &&
    !hasScheduledMeeting(deal)
  ) {
    return {
      allowed: false,
      code: 'REUNIAO_REQUIRED',
      reason: REUNIAO_REQUIRED_MESSAGE,
      missingFields: [REUNIAO_REQUIRED_FIELD],
    }
  }

  if (currentStage === 'reuniao_agendada' && targetStage === 'reuniao_realizada' && !hasRealizedReuniao(deal)) {
    return {
      allowed: false,
      code: 'REUNIAO_NOT_CONFIRMED',
      reason: 'Confirme a reunião como realizada no painel do deal antes de avançar.',
    }
  }

  if (isCloserBlockedGerarContratoExit(currentStage, targetStage, userRole)) {
    return {
      allowed: false,
      code: 'FINANCE_DRIVEN_STAGE',
      reason: FINANCE_GERAR_CONTRATO_MESSAGE,
    }
  }

  if (blocksPropostaEnviada(currentStage, targetStage, deal.plataforma)) {
    return {
      allowed: false,
      code: 'PLATAFORMA_REQUIRED',
      reason: PLATAFORMA_REQUIRED_MESSAGE,
      missingFields: ['plataforma'],
    }
  }

  const requiredFields = STAGE_REQUIRED_FIELDS[targetStage] ?? []
  const missingFields = requiredFields.filter((field) => !hasValue(deal[field]))

  if (missingFields.length > 0) {
    const labels = missingFields.map((f) => FIELD_LABELS[f] ?? f)
    return {
      allowed: false,
      code: 'REQUIRED_FIELD_MISSING',
      reason: `Campos obrigatórios faltando: ${labels.join(', ')}`,
      missingFields: labels,
    }
  }

  if (targetStage === 'ganho' && Number(deal.valor) <= 0) {
    return {
      allowed: false,
      code: 'ZERO_VALUE',
      reason: 'Não é possível marcar como ganho um deal com valor zerado',
    }
  }

  return { allowed: true }
}

export function getAllowedTransitions(stage: DealStage, userRole?: string | null): DealStage[] {
  return (VALID_TRANSITIONS[stage] ?? []).filter(
    (target) => !isCloserBlockedGerarContratoExit(stage, target, userRole),
  )
}
